import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import { usePublicLanguage } from '../../contexts/PublicLanguageContext';
import ProcessStepsSimplified from '../../components/home/ProcessStepsSimplified';
import AppointmentSection from '../../components/home/AppointmentSection';
import PublicFooter from '../../components/PublicFooter';
import {
  PublicHeroShell,
  PublicSectionHeader,
  PublicFeatureCard,
  PublicCtaBand
} from '../../components/public';
import { communityAPI, handleApiError } from '../../utils/api';
import { publicLayout, publicUi } from '../../styles/publicDesignSystem';
import {
  Brain,
  Zap,
  Users,
  BarChart3,
  CheckCircle,
  ArrowRight,
  Clock,
  TrendingUp,
  Target,
  AlertTriangle,
  Repeat,
  DollarSign,
  Award,
  Shield,
  Mail,
  Calculator,
  ChevronRight,
} from 'lucide-react';

const HomePage = () => {
  const { t, currentLanguage } = usePublicLanguage();
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [selectedPainPoint, setSelectedPainPoint] = useState(0);

  const painPointStickerStyles = [
    { tone: 'painpoint-sticker-amber', rotation: 'painpoint-sticker-r1' },
    { tone: 'painpoint-sticker-blue', rotation: 'painpoint-sticker-r2' },
    { tone: 'painpoint-sticker-green', rotation: 'painpoint-sticker-r3' },
    { tone: 'painpoint-sticker-rose', rotation: 'painpoint-sticker-r4' }
  ];

  const painPoints = [
    {
      icon: Clock,
      title: t('home.painPoints.timeWasting.title'),
      description: t('home.painPoints.timeWasting.description')
    },
    {
      icon: AlertTriangle,
      title: t('home.painPoints.bias.title'),
      description: t('home.painPoints.bias.description')
    },
    {
      icon: Repeat,
      title: t('home.painPoints.inconsistency.title'),
      description: t('home.painPoints.inconsistency.description')
    },
    {
      icon: TrendingUp,
      title: t('home.painPoints.tracking.title'),
      description: t('home.painPoints.tracking.description')
    }
  ];

  const features = [
    {
      icon: Brain,
      title: t('home.features.aiAnalysis.title'),
      description: t('home.features.aiAnalysis.description'),
      benefit: t('home.features.aiAnalysis.benefit')
    },
    {
      icon: Zap,
      title: t('home.features.timeGain.title'),
      description: t('home.features.timeGain.description'),
      benefit: t('home.features.timeGain.benefit')
    },
    {
      icon: Users,
      title: t('home.features.collaboration.title'),
      description: t('home.features.collaboration.description'),
      benefit: t('home.features.collaboration.benefit')
    },
    {
      icon: BarChart3,
      title: t('home.features.datadriven.title'),
      description: t('home.features.datadriven.description'),
      benefit: t('home.features.datadriven.benefit')
    }
  ];

  const stats = [
    { value: '90%', label: t('home.roi.stats.timesSaved'), icon: Clock },
    { value: '500+', label: t('home.roi.stats.cvsHour'), icon: Zap },
    { value: '95%', label: t('home.roi.stats.precision'), icon: Target },
    { value: '24/7', label: t('home.roi.stats.availability'), icon: Shield }
  ];

  const roiBenefits = [
    {
      icon: DollarSign,
      title: t('home.roi.benefits.measurableRoi.title'),
      description: t('home.roi.benefits.measurableRoi.description'),
      value: t('home.roi.benefits.measurableRoi.value')
    },
    {
      icon: Award,
      title: t('home.roi.benefits.quality.title'),
      description: t('home.roi.benefits.quality.description'),
      value: t('home.roi.benefits.quality.value')
    },
    {
      icon: Clock,
      title: t('home.roi.benefits.timeToHire.title'),
      description: t('home.roi.benefits.timeToHire.description'),
      value: t('home.roi.benefits.timeToHire.value')
    },
    {
      icon: Users,
      title: t('home.roi.benefits.satisfaction.title'),
      description: t('home.roi.benefits.satisfaction.description'),
      value: t('home.roi.benefits.satisfaction.value')
    }
  ];
  const featureStickyTones = ['hero-sticky-amber', 'hero-sticky-blue', 'hero-sticky-green', 'hero-sticky-rose'];

  const activePainPoint = painPoints[selectedPainPoint] || painPoints[0];

  const handleWaitlistSubmit = async (event) => {
    event.preventDefault();
    const normalizedEmail = waitlistEmail.trim().toLowerCase();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!normalizedEmail || !emailRegex.test(normalizedEmail)) {
      toast.error(t('home.community.form.invalidEmail'));
      return;
    }

    setWaitlistLoading(true);
    try {
      const response = await communityAPI.joinWaitlist({
        email: normalizedEmail,
        language: currentLanguage,
        source: 'homepage',
        page: '/'
      });
      toast.success(response?.data?.message || t('home.community.form.success'));
      setWaitlistEmail('');
    } catch (error) {
      toast.error(handleApiError(error, t('home.community.form.error')));
    } finally {
      setWaitlistLoading(false);
    }
  };

  const heroNotes = [
    {
      key: 'screening',
      tone: 'hero-sticky-amber',
      position: 'hero-note-1',
      rotation: -6,
      title: t('home.hero.whiteboard.notes.screening.title'),
      body: t('home.hero.whiteboard.notes.screening.body')
    },
    {
      key: 'followup',
      tone: 'hero-sticky-blue',
      position: 'hero-note-2',
      rotation: 4,
      title: t('home.hero.whiteboard.notes.followup.title'),
      body: t('home.hero.whiteboard.notes.followup.body')
    },
    {
      key: 'interview',
      tone: 'hero-sticky-green',
      position: 'hero-note-3',
      rotation: -3,
      title: t('home.hero.whiteboard.notes.interview.title'),
      body: t('home.hero.whiteboard.notes.interview.body')
    },
    {
      key: 'feedback',
      tone: 'hero-sticky-rose',
      position: 'hero-note-4',
      rotation: 5,
      title: t('home.hero.whiteboard.notes.feedback.title'),
      body: t('home.hero.whiteboard.notes.feedback.body')
    },
    {
      key: 'pipeline',
      tone: 'hero-sticky-violet',
      position: 'hero-note-5',
      rotation: -5,
      title: t('home.hero.whiteboard.notes.pipeline.title'),
      body: t('home.hero.whiteboard.notes.pipeline.body')
    },
    {
      key: 'sla',
      tone: 'hero-sticky-amber',
      position: 'hero-note-6',
      rotation: 3,
      title: t('home.hero.whiteboard.notes.sla.title'),
      body: t('home.hero.whiteboard.notes.sla.body')
    },
    {
      key: 'sourcing',
      tone: 'hero-sticky-blue',
      position: 'hero-note-7',
      rotation: -4,
      title: t('home.hero.whiteboard.notes.sourcing.title'),
      body: t('home.hero.whiteboard.notes.sourcing.body')
    },
    {
      key: 'sync',
      tone: 'hero-sticky-green',
      position: 'hero-note-8',
      rotation: 2,
      title: t('home.hero.whiteboard.notes.sync.title'),
      body: t('home.hero.whiteboard.notes.sync.body')
    },
    {
      key: 'calendar',
      tone: 'hero-sticky-rose',
      position: 'hero-note-9',
      rotation: -2,
      title: t('home.hero.whiteboard.notes.calendar.title'),
      body: t('home.hero.whiteboard.notes.calendar.body')
    },
    {
      key: 'debrief',
      tone: 'hero-sticky-violet',
      position: 'hero-note-10',
      rotation: 4,
      title: t('home.hero.whiteboard.notes.debrief.title'),
      body: t('home.hero.whiteboard.notes.debrief.body')
    },
    {
      key: 'response',
      tone: 'hero-sticky-amber',
      position: 'hero-note-11',
      rotation: -3,
      title: t('home.hero.whiteboard.notes.response.title'),
      body: t('home.hero.whiteboard.notes.response.body')
    },
    {
      key: 'pipelineHealth',
      tone: 'hero-sticky-blue',
      position: 'hero-note-12',
      rotation: 3,
      title: t('home.hero.whiteboard.notes.pipelineHealth.title'),
      body: t('home.hero.whiteboard.notes.pipelineHealth.body')
    },
    {
      key: 'sync-2',
      tone: 'hero-sticky-green',
      position: 'hero-note-15',
      rotation: -3,
      title: t('home.hero.whiteboard.notes.sync.title'),
      body: t('home.hero.whiteboard.notes.sync.body')
    },
    {
      key: 'feedback-2',
      tone: 'hero-sticky-rose',
      position: 'hero-note-16',
      rotation: 3,
      title: t('home.hero.whiteboard.notes.feedback.title'),
      body: t('home.hero.whiteboard.notes.feedback.body')
    }
  ];

  const heroWhiteboardLayer = (
    <div className="hero-whiteboard-layer" aria-hidden="true">
      <div className="hero-whiteboard-grid" />
      <div className="hero-whiteboard-glare" />
      <div className="absolute inset-0">
        {heroNotes.map((note, index) => (
          <motion.article
            key={note.key}
            initial={{ opacity: 0, y: 18, rotate: note.rotation - 3 }}
            animate={{ opacity: 1, y: 0, rotate: note.rotation }}
            transition={{ delay: 0.28 + index * 0.08, duration: 0.45, ease: 'easeOut' }}
            className={`hero-sticky-note ${note.tone} ${note.position}`}
          >
            <span className="hero-sticky-pin" />
            <span className="hero-sticky-fold" />
            <p className="hero-sticky-title">{note.title}</p>
            <p className="hero-sticky-body">{note.body}</p>
          </motion.article>
        ))}
      </div>
    </div>
  );

  return (
    <div className={publicLayout.page}>
      <PublicHeroShell
        variant="whiteboard"
        backgroundLayer={heroWhiteboardLayer}
        contentAlign="center"
        title={t('home.hero.title')}
        titleHighlight={t('home.hero.titleHighlight')}
        subtitle={t('home.hero.subtitle')}
        subtitleHighlight={t('home.hero.subtitleHighlight')}
        primaryAction={(
          <Link
            to="/signup"
            className="group hero-cta-handwritten inline-flex items-center justify-center px-8 py-4 bg-yellow-500 text-gray-900 hover:bg-yellow-400 font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
          >
            {t('home.hero.ctaBeta')}
            <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        )}
      />

      <section className={`${publicLayout.sectionMuted} painpoints-handwritten-section`}>
        <div className="painpoints-hero-transition" aria-hidden="true" />
        <div className={publicLayout.container}>
          <PublicSectionHeader
            badge={t('home.painPoints.badge')}
            badgeIcon={AlertTriangle}
            title={t('home.painPoints.title')}
            subtitle={t('home.painPoints.subtitle')}
            className="painpoints-section-header"
          />

          <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 items-start">
            <div className="lg:col-span-5 space-y-3">
              {painPoints.map((painPoint, index) => {
                const isActive = index === selectedPainPoint;
                const stickerStyle = painPointStickerStyles[index % painPointStickerStyles.length];
                return (
                  <button
                    key={painPoint.title}
                    type="button"
                    onClick={() => setSelectedPainPoint(index)}
                    onMouseEnter={() => setSelectedPainPoint(index)}
                    className={`painpoint-sticker ${stickerStyle.tone} ${stickerStyle.rotation} ${isActive ? 'painpoint-sticker-active' : ''}`}
                    aria-pressed={isActive}
                  >
                    <span className="painpoint-sticker-pin" />
                    <span className="painpoint-sticker-fold" />
                    <div className="flex items-start gap-3">
                      <div className={`painpoint-sticker-icon ${isActive ? 'painpoint-sticker-icon-active' : ''}`}>
                        <painPoint.icon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 text-left">
                        <p className="text-base font-semibold text-slate-900 pr-3">{painPoint.title}</p>
                      </div>
                    </div>
                    <ChevronRight className={`h-4 w-4 transition ${isActive ? 'text-primary-600' : 'text-slate-300'}`} />
                  </button>
                );
              })}
            </div>

            <div className="lg:col-span-7">
              <motion.div
                key={activePainPoint.title}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, ease: 'easeOut' }}
                className="painpoint-torn-stage"
              >
                <div className="painpoint-torn-shadow" aria-hidden="true" />
                <article className="painpoint-torn-paper">
                  <div className="painpoint-torn-content">
                    <p className="painpoint-torn-title">{activePainPoint.title}</p>
                    <p className="painpoint-torn-line">{activePainPoint.description}</p>
                    <p className="painpoint-torn-line">{t('home.painPoints.solution')}</p>
                    <p className="painpoint-torn-signature">{t('home.painPoints.preview.signature')}</p>
                  </div>
                </article>
              </motion.div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, delay: 0.2 }}
            className="flex justify-center mt-12"
          >
            <article className="painpoint-pinned-note painpoint-pinned-note-sway">
              <span className="painpoint-pinned-pin" />
              <span className="painpoint-pinned-fold" />
              <p className="painpoint-pinned-title">{t('home.painPoints.conclusion')}</p>
              <p className="painpoint-pinned-body">{t('home.painPoints.solution')}</p>
            </article>
          </motion.div>
        </div>
      </section>

      <ProcessStepsSimplified />

      <AppointmentSection />

      <section className={`${publicLayout.sectionSurface} features-handwritten-section`}>
        <div className={`${publicLayout.container} relative z-10`}>
          <PublicSectionHeader
            title={t('home.features.title')}
            subtitle={t('home.features.subtitle')}
            className="features-handwritten-header"
          />

          <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.article
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: index * 0.08 }}
                className={`features-sticky-card ${featureStickyTones[index % featureStickyTones.length]} features-sticky-tilt-${index % 4}`}
              >
                <span className="hero-sticky-pin" />
                <span className="hero-sticky-fold" />
                <div className="features-sticky-icon-wrap" aria-hidden="true">
                  <feature.icon className="h-7 w-7 features-sticky-icon" />
                </div>
                <h3 className="features-sticky-title">{feature.title}</h3>
                <p className="features-sticky-description">{feature.description}</p>
                <div className="features-sticky-tag">{feature.benefit}</div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <section className={`${publicLayout.sectionMuted} roi-handwritten-section`}>
        <div className={publicLayout.container}>
          <PublicSectionHeader
            badge={t('home.roi.badge')}
            badgeIcon={TrendingUp}
            title={t('home.roi.title')}
            subtitle={t('home.roi.subtitle')}
            className="roi-section-header"
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: index * 0.06 }}
                className={`${publicUi.cardInteractive} p-6 text-center`}
              >
                <div className="bg-gradient-to-br from-primary-500 to-primary-600 w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <stat.icon className="h-6 w-6 text-white" />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-2">{stat.value}</div>
                <div className="text-gray-600">{stat.label}</div>
              </motion.div>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {roiBenefits.map((benefit, index) => (
              <PublicFeatureCard
                key={benefit.title}
                icon={benefit.icon}
                title={benefit.title}
                description={benefit.description}
                tag={benefit.value}
                index={index}
                iconContainerClass="bg-green-100 text-green-600"
              />
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45 }}
            className="mt-16 roi-calculator-paper rounded-2xl p-8 lg:p-12 text-slate-900"
          >
            <div className="grid lg:grid-cols-3 gap-8 items-center">
              <div className="lg:col-span-2">
                <h3 className="text-2xl lg:text-3xl font-bold mb-4 roi-calculator-title">{t('home.roi.calculator.title')}</h3>
                <p className="text-lg mb-6 roi-calculator-subtitle">{t('home.roi.calculator.subtitle')}</p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    to="/signup"
                    className="inline-flex items-center px-6 py-3 bg-yellow-500 text-gray-900 font-semibold rounded-lg hover:bg-yellow-400 transition-all duration-200"
                  >
                    <Calculator className="mr-2 h-5 w-5" />
                    {t('home.roi.calculator.ctaCalculate')}
                  </Link>
                  <Link
                    to="/contact"
                    className="inline-flex items-center px-6 py-3 border-2 border-slate-300 text-slate-700 hover:bg-slate-50 rounded-lg transition-all duration-200"
                  >
                    {t('home.roi.calculator.ctaDemo')}
                  </Link>
                </div>
              </div>
              <div className="text-center lg:text-right">
                <div className="text-4xl lg:text-5xl font-bold text-yellow-400 mb-2">{t('home.roi.calculator.roiValue')}</div>
                <div className="text-slate-600">{t('home.roi.calculator.roiDescription')}</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className={`${publicLayout.sectionSurface} community-handwritten-section`}>
        <div className={`${publicLayout.container} max-w-6xl`}>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-3xl community-paper-card p-8 lg:p-12 shadow-2xl"
          >
            <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-10 items-start lg:items-center">
              <div>
                <div className="inline-flex items-center community-badge-sticker mb-5">
                  <Users className="h-4 w-4 mr-2" />
                  {t('home.community.badge')}
                </div>
                <h2 className="text-3xl lg:text-4xl font-bold leading-tight mb-4 community-title">
                  {t('home.community.title')}
                </h2>
                <p className="text-lg leading-relaxed mb-6 community-subtitle">
                  {t('home.community.subtitle')}
                </p>
                <div className="space-y-3 community-points">
                  <div className="flex items-start community-point-row">
                    <CheckCircle className="h-5 w-5 mr-3 mt-0.5 community-point-icon flex-shrink-0" />
                    <span>{t('home.community.points.testing')}</span>
                  </div>
                  <div className="flex items-start community-point-row">
                    <CheckCircle className="h-5 w-5 mr-3 mt-0.5 community-point-icon flex-shrink-0" />
                    <span>{t('home.community.points.support')}</span>
                  </div>
                  <div className="flex items-start community-point-row">
                    <CheckCircle className="h-5 w-5 mr-3 mt-0.5 community-point-icon flex-shrink-0" />
                    <span>{t('home.community.points.quebec')}</span>
                  </div>
                </div>
              </div>

              <form
                onSubmit={handleWaitlistSubmit}
                className="rounded-2xl community-form-paper p-5 lg:p-6"
              >
                <label htmlFor="waitlist-email" className="block text-sm font-semibold community-form-label mb-3">
                  {t('home.community.form.label')}
                </label>
                <div className="space-y-3">
                  <div className="relative">
                    <Mail className="h-4 w-4 community-mail-icon absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      id="waitlist-email"
                      type="email"
                      value={waitlistEmail}
                      onChange={(event) => setWaitlistEmail(event.target.value)}
                      placeholder={t('home.community.form.placeholder')}
                      className="w-full rounded-xl community-input pl-9 pr-3 py-3 focus:outline-none focus:ring-2 focus:ring-yellow-300 focus:border-transparent"
                      disabled={waitlistLoading}
                      autoComplete="email"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={waitlistLoading}
                    className="w-full inline-flex items-center justify-center rounded-xl community-form-cta px-4 py-3 text-sm font-semibold disabled:opacity-70 disabled:cursor-not-allowed transition-colors"
                  >
                    {waitlistLoading ? t('home.community.form.loading') : t('home.community.form.cta')}
                  </button>
                </div>
                <p className="mt-3 text-xs community-form-disclaimer">{t('home.community.form.disclaimer')}</p>
              </form>
            </div>
          </motion.div>
        </div>
      </section>

      <PublicCtaBand
        sectionVariant="surface"
        className="public-cta-handwritten"
        badge={t('home.cta.badge')}
        badgeIcon={Award}
        title={t('home.cta.title')}
        titleHighlight={t('home.cta.titleHighlight')}
        subtitle={t('home.cta.subtitle')}
        subtitleHighlight={t('home.cta.subtitleHighlight')}
        primaryAction={(
          <Link
            to="/signup"
            className="group inline-flex items-center px-10 py-5 bg-yellow-500 text-gray-900 hover:bg-yellow-400 font-bold text-lg rounded-xl transition-all duration-200 transform hover:scale-105 shadow-2xl hover:shadow-yellow-500/25"
          >
            {t('home.cta.button')}
            <ArrowRight className="ml-3 h-6 w-6 group-hover:translate-x-1 transition-transform" />
          </Link>
        )}
        footerItems={[
          { key: 'trial', icon: CheckCircle, text: t('home.cta.guarantees.trial') },
          { key: 'instant', icon: CheckCircle, text: t('home.cta.guarantees.instant') },
          { key: 'support', icon: CheckCircle, text: t('home.cta.guarantees.support') }
        ]}
      />

      <PublicFooter />
    </div>
  );
};

export default HomePage;
